import { Button } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useMemo } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { v4 as uuidv4 } from "uuid";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";
import { SortableItem } from "./components/SortableItem";
import { useInvoicesClientDefaultsPageContext } from "../../context/useInvoicesClientDefaultsPageContext";

export const ClientDefaultsForm = () => {
  const { t } = useTranslations();
  const { items, dispatchItemsReducer, submitForm } =
    useInvoicesClientDefaultsPageContext();

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.pos - b.pos),
    [items]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedItems.findIndex((item) => item.id === active.id);
      const newIndex = sortedItems.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(sortedItems, oldIndex, newIndex).map(
        (item, index) => ({
          ...item,
          pos: index,
        })
      );

      dispatchItemsReducer({
        type: "REORDER_ITEMS",
        payload: newItems,
      });

      submitForm();
    }
  };

  const handleAddNewField = () => {
    if (sortedItems.length >= 9) return;

    dispatchItemsReducer({
      type: "ADD_ITEM",
      payload: {
        id: uuidv4(),
        name: "New Field",
        visible: true,
        pos: sortedItems.length,
        type: "custom",
      },
    });
  };

  return (
    <div className="jugl__custom-scrollbar max-h-[calc(100vh-16.2rem)] overflow-x-visible overflow-y-auto py-8">
      <div className="rounded-2xl bg-white px-8 py-9">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="m-0 flex flex-wrap font-secondary text-xl font-medium">
            <span className="pr-1 whitespace-nowrap">
              {t({
                id: "invoices.clientDefaults.title",
                defaultMessage: "Select Customer Attributes",
              })}
            </span>

            <span className="whitespace-nowrap text-grey-700">(Up to 9)</span>
          </h2>

          <div>
            <Button
              iconEnd={<PlusIcon />}
              onClick={handleAddNewField}
              isDisabled={sortedItems.length >= 9}
            >
              Add New Field
            </Button>
          </div>
        </div>

        <div>
          <div className="mt-8 grid min-w-fit grid-cols-[auto_100px] grid-rows-[min-content_1fr] rounded-t-xl border border-b-0 border-solid border-[#EEF2F5]">
            <div className="px-4 py-3 font-secondary text-xs text-grey-800">
              <span className="w-full truncate">Field Name</span>
            </div>
            <div className="border-0 border-l border-solid border-[#EEF2F5] px-4 py-3 font-secondary text-xs text-grey-800">
              <span className="w-full truncate">Visible?</span>
            </div>
          </div>

          <div className="rounded-b-xl border border-t-0 border-solid border-[#EEF2F5]">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedItems}
                strategy={verticalListSortingStrategy}
              >
                {sortedItems.map((item) => (
                  <SortableItem
                    key={item.id}
                    item={item}
                    onVisibilityChange={(visible) => {
                      dispatchItemsReducer({
                        type: "MODIFY_ITEM",
                        payload: {
                          id: item.id,
                          newData: { visible },
                        },
                      });

                      submitForm();
                    }}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>
    </div>
  );
};
